import {useEffect} from 'react';
import * as Sentry from '@sentry/react';
import {useAppSelector} from 'src/redux/hooks';
import {showAlert} from 'src/shared/hooks/useAlert';
import {isEffectivelyOffline, subscribeConnectivity} from './connectivity';
import type {HydrationPlanEntry} from './offlineSessionManager';
import {
  currentIdentity,
  getActiveSession,
  markSessionReady,
  rehydrateSession,
  startOfflineSession,
} from './offlineSessionManager';
import {readSnapshot} from './offlineQuery';
import {ensureCapacity, QuotaError} from './quota';

export interface EnsureDomainSessionOptions {
  /** Re-run hydration into an existing ready session to pick up server-side
   * changes. Without `force` this only happens once the cache is stale. */
  refresh?: boolean;
  /** Skip the staleness check and always re-hydrate; surfaces its errors to
   * the caller instead of swallowing them. Used by manual refresh actions. */
  force?: boolean;
  /** Never start a fresh session — only maintain one that already exists.
   * Fresh starts belong to the domain's auto-session hook. */
  existingOnly?: boolean;
}

export interface DomainSessionConfig<TBundle> {
  contextKey: string;
  /** False when the domain's feature flags are off for the instance. */
  isEnabled: () => boolean;
  fetchBundle: () => Promise<TBundle>;
  buildPlan: (bundle: TBundle) => HydrationPlanEntry[];
  estimateBytes: (bundle: TBundle) => number;
  /** Snapshot key whose `cachedAt` decides staleness for the whole session. */
  staleKey: string;
  maxAgeMs: number;
  storageAlertText: string;
  /** Shown when a forced refresh is attempted while offline. */
  offlineAlertText: string;
  /** Runs after the snapshots land and before the session is marked ready —
   * the seam for blob downloads that must complete before offline reads. */
  afterHydrate?: (sessionId: string, bundle: TBundle) => Promise<void>;
}

export interface DomainSession {
  ensure: (options?: EnsureDomainSessionOptions) => Promise<void>;
  subscribe: (listener: () => void) => () => void;
  isRefreshing: () => boolean;
  isStale: (sessionId: string) => Promise<boolean>;
  startScheduler: () => () => void;
  stopScheduler: () => void;
  isSchedulerStarted: () => boolean;
  useAutoSession: () => void;
}

const CAPTURE_HEADROOM_BYTES = 20_000_000;

/**
 * Builds one auto-cached offline session: its ensure/refresh lifecycle, the
 * app-lifetime staleness scheduler, and the hook that starts it after sign-in.
 */
export const createDomainSession = <TBundle>(
  config: DomainSessionConfig<TBundle>
): DomainSession => {
  // One in-flight ensure at a time so a mount and a reconnect event don't both
  // fetch the bundle or race the duplicate-session guard.
  let inFlight: Promise<void> | undefined;

  // Chain tail and live-run count: a forced refresh runs after the in-flight
  // one, and the refreshing flag stays true until the last chained run settles.
  let tail: Promise<void> = Promise.resolve();
  let activeRuns = 0;

  // Storage-full is warned once per page load; further attempts stay silent.
  let quotaWarned = false;

  const listeners = new Set<() => void>();

  const setInFlight = (value: Promise<void> | undefined): void => {
    inFlight = value;
    listeners.forEach(listener => listener());
  };

  const subscribe = (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  const isRefreshing = (): boolean => inFlight !== undefined;

  const isStale = async (sessionId: string): Promise<boolean> => {
    const snapshot = await readSnapshot(sessionId, config.staleKey);
    if (!snapshot) return true;
    const cachedAt = new Date(snapshot.cachedAt).getTime();
    return Number.isNaN(cachedAt) || Date.now() - cachedAt >= config.maxAgeMs;
  };

  const handleEnsureError = (error: unknown): void => {
    if (error instanceof QuotaError) {
      if (quotaWarned) return;
      quotaWarned = true;
      showAlert(config.storageAlertText, 'warning');
      return;
    }
    Sentry.captureException(error);
  };

  const hydrateExistingSession = async (sessionId: string): Promise<void> => {
    const bundle = await config.fetchBundle();
    await rehydrateSession(sessionId, config.buildPlan(bundle));
    await config.afterHydrate?.(sessionId, bundle);
  };

  const startFreshSession = async (): Promise<void> => {
    const bundle = await config.fetchBundle();
    await ensureCapacity(config.estimateBytes(bundle), CAPTURE_HEADROOM_BYTES);
    const session = await startOfflineSession({
      contextKey: config.contextKey,
      hydrationPlan: config.buildPlan(bundle),
    });
    await config.afterHydrate?.(session.id, bundle);
    await markSessionReady(session.id);
  };

  // Cross-tab guard: blocking (not ifAvailable) so a second tab's refresh waits
  // its turn rather than being dropped. Falls back where Web Locks is missing.
  const withCacheLock = async (run: () => Promise<void>): Promise<void> => {
    if (typeof navigator === 'undefined' || !navigator.locks) {
      return run();
    }
    await navigator.locks.request(`proact-offline-cache:${config.contextKey}`, run);
  };

  const runEnsure = async (
    options: EnsureDomainSessionOptions
  ): Promise<void> => {
    try {
      const session = await getActiveSession(config.contextKey);
      if (session?.ready) {
        const stale =
          options.force ||
          (options.refresh === true && (await isStale(session.id)));
        if (stale) await hydrateExistingSession(session.id);
        return;
      }
      // A not-ready session is a zombie from a prior interrupted start —
      // re-hydrate and finish it rather than leaving it stuck forever.
      if (session) {
        await hydrateExistingSession(session.id);
        await markSessionReady(session.id);
        return;
      }
      if (options.existingOnly) return;
      await startFreshSession();
    } catch (error) {
      handleEnsureError(error);
      // Quota already alerted once; anything else is the button's to report.
      if (options.force && !(error instanceof QuotaError)) throw error;
    }
  };

  // A forced refresh queues behind whatever is running so it never adopts a
  // result fetched before the user asked for one.
  const chainRun = (options: EnsureDomainSessionOptions): Promise<void> => {
    const previous = tail;
    activeRuns += 1;
    const run = (async () => {
      await previous;
      try {
        await withCacheLock(() => runEnsure(options));
      } finally {
        activeRuns -= 1;
        if (activeRuns === 0) setInFlight(undefined);
      }
    })();
    tail = run.then(
      () => undefined,
      () => undefined
    );
    setInFlight(run);
    return run;
  };

  // Best-effort auto-cache: no-op offline, before sign-in, or without the
  // domain's feature. Errors are swallowed unless `force` asked for them.
  const ensure = async (
    options: EnsureDomainSessionOptions = {}
  ): Promise<void> => {
    if (isEffectivelyOffline()) {
      if (options.force) showAlert(config.offlineAlertText, 'warning');
      return;
    }
    if (!currentIdentity() || !config.isEnabled()) return;
    if (inFlight && !options.force) return inFlight;

    return chainRun(options);
  };

  let started = false;
  let stop: (() => void) | undefined;

  // Each tick is a no-op unless the cache is stale; offline, signed-out, and
  // flag-less cases are handled by `ensure`.
  const refreshIfStale = (): void => {
    void ensure({refresh: true, existingOnly: true});
  };

  const stopScheduler = (): void => {
    stop?.();
    stop = undefined;
    started = false;
  };

  // App-lifetime cache refresher: an initial pass, then passes on reconnect,
  // tab-visible, and a max-age heartbeat. Idempotent.
  const startScheduler = (): (() => void) => {
    if (started) return stopScheduler;
    started = true;

    refreshIfStale();

    const unsubscribeConnectivity = subscribeConnectivity(() => {
      if (!isEffectivelyOffline()) refreshIfStale();
    });

    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') refreshIfStale();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    const interval = setInterval(refreshIfStale, config.maxAgeMs);

    stop = () => {
      unsubscribeConnectivity();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      clearInterval(interval);
    };

    return stopScheduler;
  };

  // Starts the auto-cached session once signed in (idempotent, guarded).
  // `refresh` is a no-op unless the cache is past its max age.
  const useAutoSession = (): void => {
    const userId = useAppSelector(state => state.session?.user?.id);
    const instanceId = useAppSelector(state => state.session?.instance?.id);

    useEffect(() => {
      if (userId != null && instanceId != null) {
        void ensure({refresh: true});
      }
    }, [userId, instanceId]);
  };

  return {
    ensure,
    subscribe,
    isRefreshing,
    isStale,
    startScheduler,
    stopScheduler,
    isSchedulerStarted: () => started,
    useAutoSession,
  };
};
